import {
  AccessTime as AccessTimeIcon,
  ContentCopy as ContentCopyIcon,
  Facebook as FacebookIcon,
  Favorite,
  FavoriteBorder,
  Share as ShareIcon,
  Twitter as TwitterIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import {
  Alert, // Para Snackbar
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip, // *** Añadir Chip para etiquetas ***
  IconButton, // Para opciones del menú
  ListItemIcon, // Para iconos en opciones
  ListItemText, // Para texto en opciones // Para acceder al tema
  Menu, // Para menú de compartir
  MenuItem, // Para botón Like
  Snackbar, // Para feedback like
  Tooltip, // Para botón Like
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useState } from "react"; // *** Añadir useMemo ***
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext"; // Ajusta la ruta si es necesario
import { recordPostLike } from "../../services/analytics"; // Importar SOLO funciones específicas que existan
import { stripMarkdown } from "../../utils/textUtils"; // Utilidad para limpiar Markdown

// Función para verificar si un post es válido
const isValidPost = (post) => {
  return post && typeof post === "object" && post.id;
};

const BlogPostCard = ({ post }) => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const theme = useTheme();

  // ... (Estados y lógica de like/snackbar sin cambios) ...
  const [userLiked, setUserLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post?.likes || 0);
  const [isLiking, setIsLiking] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const shareMenuOpen = Boolean(shareAnchorEl);

  useEffect(() => {
    let isMounted = true;

    // *** ELIMINAR O COMENTAR ESTE BLOQUE ENTERO ***
    // if (currentUser && post?.id) {
    //   checkIfUserLikedPost(post.id, currentUser.uid) // <-- ESTA ES LA LLAMADA PROBLEMÁTICA
    //     .then((liked) => {
    //       if (isMounted) {
    //         setUserLiked(liked);
    //       }
    //     })
    //     .catch((err) => {
    //       console.error('Error checking like status:', err);
    //     });
    // } else {
    //   if (isMounted) {
    //     setUserLiked(false);
    //   }
    // }
    // *** FIN DEL BLOQUE A ELIMINAR/COMENTAR ***

    // Dejar solo la parte que establece el contador inicial
    if (isMounted) {
      // Puedes asumir false inicialmente para userLiked si no tienes otra fuente
      setUserLiked(false);
      setLikeCount(post?.likes || 0);
    }

    return () => {
      isMounted = false;
    };
    // Ajustar dependencias si es necesario (quitar post?.likes si no se usa para userLiked)
  }, [currentUser, post?.id, post?.likes]);

  const handleCardLike = useCallback(async () => {
    if (!currentUser) {
      setSnackbar({
        open: true,
        message: t("blog.loginToLike", "Inicia sesión para dar Me gusta"),
        severity: "info",
      });
      return;
    }
    if (isLiking || !post?.id) return;

    setIsLiking(true);
    // Guardamos el estado original para comparar
    const originalLikedState = userLiked;
    const originalCount = likeCount;

    // UI optimista
    setUserLiked(!originalLikedState);
    setLikeCount(originalLikedState ? originalCount - 1 : originalCount + 1);

    try {
      const result = await recordPostLike(post.id, currentUser.uid);

      if (result.success) {
        // Aseguramos que el mensaje coincida con la acción real que hizo el usuario
        // Si originalmente no estaba marcado como "me gusta", entonces lo está añadiendo
        setSnackbar({
          open: true,
          message: !originalLikedState
            ? t("blog.likeSuccess", "¡Gracias por tu Me gusta!")
            : t("blog.unlikeSuccess", "Has quitado tu Me gusta."),
          severity: "success",
        });

        // Verificar que el servidor coincida con nuestra expectativa
        if (result.newLikedState !== !originalLikedState) {
          console.warn(
            "Inconsistencia entre estado local y servidor, ajustando UI",
          );
          setUserLiked(result.newLikedState);
          setLikeCount(result.newLikes);
        }
      } else {
        // Revertir al estado original si hubo error
        setUserLiked(originalLikedState);
        setLikeCount(originalCount);
        setSnackbar({
          open: true,
          message: result.message || t("errors.generic", "Algo salió mal"),
          severity: "error",
        });
      }
    } catch (err) {
      console.error("Error liking post:", err);
      setUserLiked(originalLikedState);
      setLikeCount(originalCount);
      setSnackbar({
        open: true,
        message: t("errors.generic", "Algo salió mal al procesar tu Me gusta"),
        severity: "error",
      });
    } finally {
      setIsLiking(false);
    }
  }, [currentUser, isLiking, post?.id, userLiked, likeCount, t]);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const formattedDate = useMemo(() => {
    if (!post?.publishedAt) return "";
    try {
      const date = post.publishedAt.seconds
        ? new Date(post.publishedAt.seconds * 1000)
        : new Date(post.publishedAt);
      return new Intl.DateTimeFormat(i18n.language || "es", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(date);
    } catch (e) {
      console.error("Error formatting date in card:", e);
      return "";
    }
  }, [post?.publishedAt, i18n.language]);

  // Calcular tiempo de lectura (~200 palabras/minuto)
  const readingTimeMinutes = useMemo(() => {
    if (post?.readTimeMinutes) return post.readTimeMinutes;
    if (post?.estimatedReadingTimeMinutes)
      return post.estimatedReadingTimeMinutes;
    if (!post?.content) return 0;
    const text = (post.content || "")
      .replace(/<[^>]*>/g, "")
      .replace(/[#*`~[\]|>-]/g, "")
      .trim();
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(wordCount / 200));
  }, [post?.content, post?.readTimeMinutes, post?.estimatedReadingTimeMinutes]);

  const imageUrl =
    post?.imageUrl || "/assets/images/general/post-placeholder.jpg";

  // Obtener categorías (asegurarse que sea un array)
  const categories = Array.isArray(post?.categories) ? post.categories : [];

  const handleShareClick = (event) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const sharePost = async (platform = null) => {
    const postUrl = `${window.location.origin}/blog/${post.slug || post.id}`; // *** USAR SLUG, FALLBACK A ID ***
    const title = post?.title || t("blog.untitledPost", "Post sin título");
    const text = post?.excerpt || "";

    try {
      if (!platform && navigator.share) {
        // Usar Web Share API si está disponible
        await navigator.share({
          title,
          text,
          url: postUrl,
        });

        setSnackbar({
          open: true,
          message: t("blog.sharedSuccessfully", "Compartido correctamente"),
          severity: "success",
        });
      } else {
        // Fallback para plataformas específicas
        let shareUrl;

        switch (platform) {
          case "facebook":
            shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              postUrl,
            )}`;
            break;
          case "twitter":
            shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
              text ? `${title} - ${text.substring(0, 80)}` : title,
            )}&url=${encodeURIComponent(postUrl)}`;
            break;
          case "whatsapp":
            shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
              `${title}\n${text ? text.substring(0, 120) + "\n" : ""}${postUrl}`,
            )}`;
            break;
          case "copy":
            await navigator.clipboard.writeText(postUrl);
            setSnackbar({
              open: true,
              message: t("blog.linkCopied", "Enlace copiado al portapapeles"),
              severity: "success",
            });
            handleShareClose();
            return;
          default:
            // Si no hay plataforma y no hay Web Share API, copiar al portapapeles
            await navigator.clipboard.writeText(postUrl);
            setSnackbar({
              open: true,
              message: t("blog.linkCopied", "Enlace copiado al portapapeles"),
              severity: "success",
            });
            handleShareClose();
            return;
        }

        // Abrir ventana para compartir
        window.open(
          shareUrl,
          "_blank",
          "noopener,noreferrer,width=600,height=400",
        );
      }
    } catch (error) {
      console.error("Error al compartir:", error);
      setSnackbar({
        open: true,
        message: t("errors.shareError", "Error al compartir"),
        severity: "error",
      });
    }

    handleShareClose();
  };

  // *** SOLUCIÓN PROFESIONAL: Props condicionales para el botón de navegación ***
  // Usa slug si existe (SEO-friendly), fallback a ID para posts antiguos
  const readMoreButtonProps = isValidPost(post)
    ? {
        component: RouterLink,
        to: `/blog/${post.slug || post.id}`,
      }
    : {
        component: "button",
      };

  return (
    <>
      <Card
        component={motion.div}
        whileHover="hover"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        variants={{
          hover: {
            y: -6,
            scale: 1.02,
            boxShadow: theme.shadows[12],
            transition: { duration: 0.2, ease: "easeOut" },
            "@media (prefers-reduced-motion: reduce)": {
              y: 0,
              scale: 1,
              boxShadow: theme.shadows[6],
            },
          },
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: theme.shadows[3],
          // Añadir soporte para color-scheme
          colorScheme: theme.palette.mode,
          // Mejorar contraste en modo oscuro
          ...(theme.palette.mode === "dark" && {
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.divider,
          }),
          // Resto de estilos
          transition:
            "transform 0.3s ease-out, box-shadow 0.3s ease-out, outline 0.1s ease-out",
          "&:focus-within": {
            outline: `3px solid ${theme.palette.secondary.main}`,
            outlineOffset: "2px",
            boxShadow: theme.shadows[8],
            transform: "translateY(-2px)",
          },
        }}
        role="article"
        aria-labelledby={`blog-post-title-${post?.id}`}
        itemScope
        itemType="https://schema.org/BlogPosting"
        onKeyDown={(e) => {
          if (e.key === "Enter" && isValidPost(post)) {
            window.location.href = `/blog/${post.slug || post.id}`; // *** USAR SLUG, FALLBACK A ID ***
          }
        }}
        tabIndex={0}
      >
        <CardMedia
          component="img"
          height="180"
          image={imageUrl}
          alt={
            post?.imageAlt ||
            t("blog.coverImageFor", "Imagen de portada para: {{title}}", {
              title: post?.title || t("blog.untitledPost"),
            })
          }
          loading="lazy"
          decoding="async"
          fetchPriority="auto"
          sx={{ objectFit: "cover" }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/assets/images/general/post-placeholder.jpg";
          }}
        />
        <CardContent
          sx={{ flexGrow: 1, pb: 1, display: "flex", flexDirection: "column" }}
        >
          {/* *** Sección de Etiquetas (Categorías) *** */}
          {categories.length > 0 && (
            <Box
              sx={{ mb: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}
              itemProp="keywords"
            >
              {categories.slice(0, 3).map(
                (
                  category, // Mostrar máx 3 etiquetas
                ) => {
                  const translatedCategory = t(
                    `blog.categories.${category}`,
                    category,
                  );
                  return (
                    <Chip
                      key={category}
                      label={translatedCategory}
                      size="small"
                      variant="outlined" // Estilo sutil
                      aria-label={t(
                        "blog.categoryTag",
                        "Categoría: {{category}}",
                        { category: translatedCategory },
                      )}
                      sx={{
                        textTransform: "capitalize",
                        fontSize: "0.75rem",
                        height: "20px",
                        cursor: "default", // No son clickables (a menos que implementes filtro)
                      }}
                    />
                  );
                },
              )}
            </Box>
          )}

          {/* Chip de tiempo de lectura */}
          {readingTimeMinutes > 0 && (
            <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              <Chip
                icon={
                  <AccessTimeIcon sx={{ fontSize: "0.85rem !important" }} />
                }
                label={t("blog.minuteRead", "{{count}} min de lectura", {
                  count: readingTimeMinutes,
                })}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: "0.7rem",
                  height: "20px",
                  borderColor: "info.main",
                  color: "info.main",
                  cursor: "default",
                  "& .MuiChip-icon": { color: "info.main" },
                }}
                aria-label={t(
                  "blog.readingTime",
                  "Tiempo de lectura: {{count}} minutos",
                  {
                    count: readingTimeMinutes,
                  },
                )}
              />
            </Box>
          )}

          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            id={`blog-post-title-${post?.id}`}
            sx={{
              fontWeight: 600,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minHeight: "3em", // Ajustar si es necesario
              mb: 1, // Reducir margen inferior
            }}
          >
            {post?.title || t("blog.untitledPost", "Post sin título")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3, // 3 líneas de descripción
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minHeight: "4.5em", // Ajustar si es necesario
              mb: 2, // Margen antes de la fecha/like
            }}
          >
            {typeof post?.excerpt === "string" && post.excerpt.trim() !== ""
              ? stripMarkdown(post.excerpt)
              : typeof post?.content === "string" && post.content.trim() !== ""
                ? stripMarkdown(post.content).substring(0, 120) + "..."
                : t("blog.noDescription", "No hay descripción disponible")}
          </Typography>

          {/* --- Info Inferior (Fecha y Like) --- */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: "auto", // Empuja al fondo del CardContent
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {formattedDate}
              {post?.readTimeMinutes &&
                ` · ${t("blog.minuteRead", "{{count}} min de lectura", { count: post.readTimeMinutes })}`}
            </Typography>
            {/* --- Botón Like Interactivo --- */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip
                title={
                  !currentUser
                    ? t("blog.loginToLike", "Inicia sesión para dar Me gusta")
                    : userLiked
                      ? t("blog.unlikeTooltip", "Quitar Me gusta")
                      : t("blog.likeTooltip", "Dar Me gusta")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleCardLike}
                    disabled={isLiking || !currentUser}
                    color={userLiked ? "error" : "default"}
                    aria-label={
                      userLiked
                        ? t("blog.unlike", "Quitar Me gusta")
                        : t("blog.like", "Me gusta")
                    }
                    sx={{
                      transition:
                        "transform 0.2s ease-in-out, background-color 0.2s",
                      // Efecto Hover
                      "&:hover:not(:disabled)": {
                        // Solo si no está deshabilitado
                        transform: "scale(1.25)", // Más grande
                        bgcolor: userLiked
                          ? "rgba(255, 82, 82, 0.1)" // Fondo rojo más claro
                          : "action.hover",
                      },
                      // Efecto Focus
                      "&:focus-visible": {
                        outline: `2px solid ${theme.palette.secondary.main}`,
                        outlineOffset: "1px",
                        transform: "scale(1.25)", // Mismo tamaño que hover
                        bgcolor: userLiked // Fondo también al enfocar
                          ? "rgba(255, 82, 82, 0.1)"
                          : "action.focus",
                      },
                      // Estilo cuando está deshabilitado
                      "&.Mui-disabled": {
                        opacity: 0.5,
                      },
                    }}
                  >
                    {userLiked ? (
                      <Favorite sx={{ fontSize: 20 }} />
                    ) : (
                      <FavoriteBorder sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Typography
                variant="body2" // Un poco más grande que caption
                color="text.secondary"
                sx={{ minWidth: "20px", textAlign: "left", fontWeight: 500 }}
              >
                {likeCount}
              </Typography>

              {/* AÑADIR BOTÓN DE COMPARTIR */}
              <Tooltip title={t("blog.share", "Compartir")}>
                <IconButton
                  size="small"
                  onClick={handleShareClick}
                  aria-label={t("blog.share", "Compartir artículo")}
                  aria-controls={shareMenuOpen ? "share-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={shareMenuOpen ? "true" : undefined}
                  sx={{
                    ml: 1,
                    transition:
                      "transform 0.2s ease-in-out, background-color 0.2s",
                    "&:hover": {
                      transform: "scale(1.25)",
                      bgcolor: "action.hover",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${theme.palette.secondary.main}`,
                      outlineOffset: "1px",
                      transform: "scale(1.25)",
                      bgcolor: "action.focus",
                    },
                  }}
                >
                  <ShareIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
        <CardActions
          sx={{
            pt: 0, // Quitar padding superior
            pb: 1.5, // Padding inferior
            px: 2, // Padding horizontal
            justifyContent: "flex-end", // Alinear botón a la derecha
          }}
        >
          {/* Botón Leer más - Usa slug si existe, fallback a ID */}
          {/* aria-label diferenciado por artículo para lectores de pantalla */}
          <Button
            size="small"
            {...readMoreButtonProps}
            itemProp="url"
            aria-label={`${t("blog.readMore", "Leer más")}: ${post?.title || ""}`}
          >
            {t("blog.readMore", "Leer más")}
          </Button>
        </CardActions>
      </Card>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        role="status"
        aria-live="polite"
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled" // Hacerlo más visible
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* AÑADIR MENÚ DE COMPARTIR */}
      <Menu
        id="share-menu"
        anchorEl={shareAnchorEl}
        open={shareMenuOpen}
        onClose={handleShareClose}
        onClick={handleShareClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: "visible",
            borderRadius: 2,
            mt: 1.5,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => sharePost()} sx={{ minWidth: 180 }}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("blog.shareNative", "Compartir")}</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => sharePost("facebook")}>
          <ListItemIcon>
            <FacebookIcon fontSize="small" sx={{ color: "#4267B2" }} />
          </ListItemIcon>
          <ListItemText>Facebook</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => sharePost("twitter")}>
          <ListItemIcon>
            <TwitterIcon fontSize="small" sx={{ color: "#1DA1F2" }} />
          </ListItemIcon>
          <ListItemText>Twitter</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => sharePost("whatsapp")}>
          <ListItemIcon>
            <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />
          </ListItemIcon>
          <ListItemText>WhatsApp</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => sharePost("copy")}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("blog.copyLink", "Copiar enlace")}</ListItemText>
        </MenuItem>
      </Menu>
      <span className="sr-only" aria-live="polite">
        {userLiked ? t("blog.youLikedThis", "Te gusta este artículo") : ""}
      </span>
    </>
  );
};

// No es necesario React.memo aquí si ya se usa en Home.js o Blog.js al importarlo
export default React.memo(BlogPostCard, (prevProps, nextProps) => {
  return (
    prevProps.post?.id === nextProps.post?.id &&
    prevProps.post?.likes === nextProps.post?.likes
  );
});
